import {addDays, isSameDay} from 'date-fns'
import {observer} from 'mobx-react-lite'
import React, {useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Time} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Sync} from '@client/sync'
import {
  TaskType,
  TaskWithColor,
  useCalendarStore
} from '@components/calendar/calendar-store'
import {noPropagate} from '@containers/hooks'
import {Sticker} from '@extensions/sticker/sticker'
import {roundNumberTo} from '../../utils'
import {useDraggableStore} from './draggable-store'

const AllDayDraggableTw = tw.div`
  absolute
  bg-main-old
  cursor-col-resize
  h-20
  right-3
  bottom-4
  w-2
  z-1000
`

export const DraggableAllDayDeadline = observer(
  // eslint-disable-next-line max-lines-per-function
  React.forwardRef(
    (
      {
        task,
        type,
        allDayDragWithoutStartDate = false
      }: {
        task: TaskWithColor
        type: TaskType
        allDayDragWithoutStartDate?: boolean
      },
      ref: React.MutableRefObject<HTMLElement>
    ) => {
      const [, store] = useCalendarStore()
      const [draggableState, draggableStore] = useDraggableStore()
      const stateRef = useRef({pos: 0, size: 0, id: null})
      const setRefState = (data: {
        pos: number
        size: number
        id: DataObjectId
      }) => {
        stateRef.current = data
      }

      const allDayInitial = (e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const clientX = e.clientX
        const resizable = ref.current as HTMLDivElement
        if (resizable) {
          const parentWidth = resizable.parentElement.parentElement.offsetWidth

          draggableStore.clearDraggableState()

          setRefState({
            pos: clientX,
            size: resizable.offsetWidth,
            id: task.task.id
          })

          draggableStore.setInitial(e.clientX, e.clientY)
          draggableStore.setTraveled(0, 0)
          draggableStore.setParentWidth(parentWidth)

          draggableStore.setDraggedItem({
            id: task.task.id,
            changeAction: 'change-deadline',
            taskType: type,
            height: null,
            width: null,
            top: null,
            backgroundColor: task.columnColor,
            gtdStage: task.task.gtdStage,
            title: task.task.title,
            isColorDark: task.isColorDark,
            assigned: task.task.assigned,
            isSubtask: task.isSubtask
          })

          document.addEventListener('mousemove', AllDayResize)
          document.addEventListener('mouseup', AllDayStopResize)
        }
      }

      const AllDayResize = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (stateRef.current.id === task.task.id) {
          const resizable = ref.current as HTMLDivElement
          if (resizable) {
            const clientX = e.clientX
            const data = stateRef.current.size + clientX - stateRef.current.pos
            if (data > draggableState.parentWidth) {
              resizable.style.width = `${roundNumberTo(
                data,
                draggableState.parentWidth
              )}px`
            }
          }
        }
      }

      // eslint-disable-next-line max-lines-per-function
      const AllDayStopResize = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        if (stateRef.current.id === task.task.id) {
          const resizable = ref.current as HTMLDivElement
          if (resizable) {
            const dayWidth = draggableState.parentWidth
            const diff = resizable.offsetWidth - stateRef.current.size
            const box = Sync.getCurrentSandbox()
            const taskHub = box.get(task.task.id) as Hub
            const day = Math.round(diff / dayWidth)
            if (taskHub instanceof Hub && day !== diff) {
              const to = Time.middleOfDay(
                Time.toTimestamp(
                  addDays(Sticker.getDeadline(taskHub).current.deadline, day)
                )
              )
              if (
                isSameDay(
                  to,
                  Time.toLocalDate(
                    Sticker.getDeadline(taskHub).current.startDate
                  )
                )
              ) {
                Sticker.getDeadline(taskHub).current.startDate = null
                Sticker.getDeadline(taskHub).current.deadline = to
                taskHub.commit()
                store.syncState(() => {
                  resizable.style.width = '100%'
                })
              } else if (allDayDragWithoutStartDate) {
                Sticker.getDeadline(taskHub).current.startDate =
                  Time.middleOfDay(
                    Sticker.getDeadline(taskHub).current.deadline
                  )

                Sticker.getDeadline(taskHub).current.deadline = to
                taskHub.commit()
                store.syncState(() => {
                  resizable.style.width = '100%'
                })
              } else {
                Sticker.getDeadline(taskHub).current.deadline = to
                taskHub.commit()
                store.syncState(() => {
                  resizable.style.width = '100%'
                })
              }
            }
          }
        }

        draggableStore.setDraggedItem({
          id: null,
          changeAction: null,
          taskType: null,
          height: null,
          width: null,
          top: null,
          backgroundColor: null,
          gtdStage: null,
          title: null,
          isColorDark: null,
          assigned: null,
          isSubtask: null
        })
        setRefState({
          pos: 0,
          size: 0,
          id: null
        })
        document.removeEventListener('mouseup', AllDayStopResize)
        document.removeEventListener('mousemove', AllDayResize)
      }

      return (
        <div
          className="absolute bottom-0 right-0 w-20 h-full cursor-col-resize"
          onMouseDown={allDayInitial}
          onClick={noPropagate}
        >
          <AllDayDraggableTw />
        </div>
      )
    }
  )
)
